import { logger } from '../../utils/logger';
import React, { useEffect, useState, useRef } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { EmailNotificationsConfig } from './EmailNotificationsConfig';
import { VerificationCodesConfig } from './VerificationCodesConfig';
import { UserManagement } from './UserManagement';
import { Roles } from './Roles';
import { Permissions } from './Permissions';
import { BackupConfig } from './BackupConfig';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useTheme } from '../../contexts/ThemeContext';
import { hasPermission } from '../../types';
import { UserIcon, SettingsIcon, BellIcon, ShieldIcon, MailIcon, UsersIcon, KeyIcon, ShieldCheckIcon, CameraIcon, EyeIcon, ChevronRightIcon, DatabaseIcon } from 'lucide-react';
interface NotificationSettings {
  newAppointments: boolean;
  cancelledAppointments: boolean;
  newPatients: boolean;
}
interface Preferences {
  darkMode: boolean;
  desktopNotifications: boolean;
}
function ReadOnlyBanner() {
  return <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl text-blue-700 dark:text-blue-300 flex items-center gap-3 text-sm shadow-sm" data-id="element-4963">
      <EyeIcon className="w-5 h-5 flex-shrink-0" data-id="element-4964" />
      <span className="font-medium" data-id="element-4965">
        Solo lectura — No tienes permisos para editar esta sección.
      </span>
    </div>;
}
export function Settings() {
  const {
    showToast
  } = useToast();
  const {
    user,
    updateUser,
    login
  } = useAuth();
  const {
    theme,
    toggleTheme
  } = useTheme();
  const userRole = user?.role || '';
  // Permission checks
  const canEditProfile = hasPermission(userRole, 'settings_profile', 'full');
  const canEditPreferences = hasPermission(userRole, 'settings_preferences', 'full');
  const canEditNotifications = hasPermission(userRole, 'settings_notifications', 'full');
  const canEditSecurity = hasPermission(userRole, 'settings_security', 'full');
  const canEditEmail = hasPermission(userRole, 'settings_email', 'full');
  const canEditVerification = hasPermission(userRole, 'settings_verification', 'full');
  // Profile state
  const [name, setName] = useState(user?.name || '');
  const [email, setEmail] = useState(user?.email || '');
  const [profileSaved, setProfileSaved] = useState(false);
  const [avatarSaved, setAvatarSaved] = useState(false);
  const [pendingAvatar, setPendingAvatar] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Sync local state when user changes in context (e.g., updated from another place)
  useEffect(() => {
    if (user?.name && user.name !== name && !profileSaved) {
      setName(user.name);
    }
    if (user?.email && user.email !== email && !profileSaved) {
      setEmail(user.email);
    }
  }, [user?.name, user?.email]);
  // Security state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordSaved, setPasswordSaved] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  // Preferences state
  const [preferences, setPreferences] = useState<Preferences>({
    darkMode: theme === 'dark',
    desktopNotifications: false
  });
  // Notification settings state
  const [notifications, setNotifications] = useState<NotificationSettings>({
    newAppointments: true,
    cancelledAppointments: true,
    newPatients: true
  });
  // Active Tab State
  const [activeTabId, setActiveTabId] = useState('profile');
  // Load preferences
  useEffect(() => {
    const savedPrefs = localStorage.getItem('savidna-preferences');
    if (savedPrefs) {
      try {
        const parsed = JSON.parse(savedPrefs);
        setPreferences(parsed);
      } catch (e) {
        logger.error('Error loading preferences:', e);
      }
    }
    const savedNotifs = localStorage.getItem('savidna-notification-settings');
    if (savedNotifs) {
      try {
        const parsed = JSON.parse(savedNotifs);
        setNotifications(parsed);
      } catch (e) {
        logger.error('Error loading notification settings:', e);
      }
    }
  }, []);
  // Save preferences
  const savePreferences = (newPrefs: Preferences) => {
    setPreferences(newPrefs);
    localStorage.setItem('savidna-preferences', JSON.stringify(newPrefs));
  };
  // Save notification settings
  const saveNotifications = (newNotifs: NotificationSettings) => {
    setNotifications(newNotifs);
    localStorage.setItem('savidna-notification-settings', JSON.stringify(newNotifs));
  };
  const handleAvatarClick = () => {
    fileInputRef.current?.click();
  };
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('La imagen es demasiado grande. El tamaño máximo es 5MB.');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result as string;
        setPendingAvatar(base64String);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleSaveAvatar = () => {
    if (!pendingAvatar) return;
    if (updateUser) {
      updateUser({
        avatar: pendingAvatar
      });
    }
    const savedUsers = localStorage.getItem('savidna-users');
    if (savedUsers) {
      try {
        const users = JSON.parse(savedUsers);
        const updatedUsers = users.map((u: any) => u.id === user?.id ? {
          ...u,
          avatar: pendingAvatar
        } : u);
        localStorage.setItem('savidna-users', JSON.stringify(updatedUsers));
      } catch (e) {
        logger.error('Error saving avatar:', e);
      }
    }
    setPendingAvatar(null);
    setAvatarSaved(true);
    setTimeout(() => setAvatarSaved(false), 3000);
  };
  const handleSaveProfile = () => {
    if (!canEditProfile) return;
    if (!name || !email) {
      showToast({
        type: 'warning',
        title: 'Campos requeridos',
        message: 'Por favor completa todos los campos.'
      });
      return;
    }
    if (updateUser) {
      updateUser({
        name,
        email
      });
    }
    const savedUsers = localStorage.getItem('savidna-users');
    if (savedUsers) {
      try {
        const users = JSON.parse(savedUsers);
        const updatedUsers = users.map((u: any) => u.id === user?.id ? {
          ...u,
          name,
          email
        } : u);
        localStorage.setItem('savidna-users', JSON.stringify(updatedUsers));
      } catch (e) {
        logger.error('Error updating user:', e);
      }
    }
    showToast({
      type: 'success',
      title: 'Perfil actualizado',
      message: 'Tu perfil fue actualizado correctamente.'
    });
    setProfileSaved(true);
    setTimeout(() => setProfileSaved(false), 3000);
  };
  const handleChangePassword = async () => {
    if (!canEditSecurity) return;
    setPasswordError('');
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('Por favor completa todos los campos');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('Las contraseñas no coinciden');
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError('La contraseña debe tener al menos 6 caracteres');
      return;
    }
    setIsChangingPassword(true);
    try {
      if (!user?.email) {
        setPasswordError('Error de sesión. Recarga la página.');
        setIsChangingPassword(false);
        return;
      }
      const loginResult = await login(user.email, currentPassword);
      if (!loginResult.success) {
        setPasswordError('La contraseña actual es incorrecta');
        setIsChangingPassword(false);
        return;
      }
      await updateUser({
        password: newPassword
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordSaved(true);
      setTimeout(() => setPasswordSaved(false), 3000);
    } catch (e) {
      logger.error('Error updating password:', e);
      setPasswordError('Error al cambiar la contraseña');
    } finally {
      setIsChangingPassword(false);
    }
  };
  const handleToggleDarkMode = (checked: boolean) => {
    if (!canEditPreferences) return;
    savePreferences({
      ...preferences,
      darkMode: checked
    });
    toggleTheme();
  };
  const handleToggleDesktopNotifications = (checked: boolean) => {
    if (!canEditPreferences) return;
    savePreferences({
      ...preferences,
      desktopNotifications: checked
    });
    if (checked && 'Notification' in window) {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification('Notificaciones activadas', {
            body: 'Ahora recibirás notificaciones de Savidna',
            icon: "/images.jpg"
          });
        }
      });
    }
  };
  // Define tabs content
  const allTabs = [{
    id: 'profile',
    label: 'Perfil',
    icon: UserIcon,
    permissionKey: 'settings_profile' as const,
    content: <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-4966">
          {!canEditProfile && <ReadOnlyBanner data-id="element-4967" />}
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-id="element-4968">
            <div className="px-8 py-8" data-id="element-4969">
              <div className="relative flex flex-col sm:flex-row justify-between items-start mb-6 gap-4" data-id="element-4970">
                <div className="flex items-center gap-6" data-id="element-4971">
                  <div className="relative group" data-id="element-4972">
                    <div className="w-24 h-24 rounded-full border-4 border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-700 overflow-hidden relative shadow-md cursor-pointer" onClick={handleAvatarClick} data-id="element-4973">
                  
                      {pendingAvatar || user?.avatar ? <img src={pendingAvatar || user?.avatar} alt={user?.name || ''} className="w-full h-full object-cover" data-id="element-4974" /> : <div className="w-full h-full flex items-center justify-center bg-teal-50 dark:bg-teal-900/50 text-teal-600 dark:text-teal-400 text-3xl font-bold" data-id="element-4975">
                          {name.charAt(0).toUpperCase()}
                        </div>}
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200" data-id="element-4976">
                        <CameraIcon className="w-8 h-8 text-white" data-id="element-4977" />
                      </div>
                    </div>
                    <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} data-id="element-4978" />
                
                  </div>
                  <div data-id="element-4979">
                    <h3 className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-4980">
                      {name}
                    </h3>
                    <p className="text-gray-500 dark:text-gray-400" data-id="element-4981">{email}</p>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2" data-id="element-4982">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 border border-teal-200 dark:border-teal-800 capitalize" data-id="element-4983">
                    {user?.role || 'Usuario'}
                  </span>
                  {pendingAvatar && <Button onClick={handleSaveAvatar} size="sm" data-id="element-4984">
                      <CameraIcon className="w-4 h-4 mr-1" data-id="element-4985" />
                      Guardar Foto
                    </Button>}
                </div>
              </div>

              {avatarSaved && <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-700 dark:text-green-300 flex items-center gap-2" data-id="element-4986">
                  <ShieldCheckIcon className="w-5 h-5" data-id="element-4987" />
                  <span className="font-medium" data-id="element-4988">
                    Foto de perfil actualizada correctamente
                  </span>
                </div>}

              {profileSaved && <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-700 dark:text-green-300 flex items-center gap-2" data-id="element-4989">
                  <ShieldCheckIcon className="w-5 h-5" data-id="element-4990" />
                  <span className="font-medium" data-id="element-4991">
                    Perfil actualizado correctamente
                  </span>
                </div>}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-4992">
                <div className="space-y-5" data-id="element-4993">
                  <Input label="Nombre Completo" value={name} onChange={e => setName(e.target.value)} placeholder="Tu nombre" disabled={!canEditProfile} data-id="element-4994" />
              
                  <Input label="Correo Electrónico" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="tu@email.com" disabled={!canEditProfile} data-id="element-4995" />
              
                </div>
                <div className="space-y-5" data-id="element-4996">
                  <Input label="Cargo / Rol" value={user?.role || 'Administrador'} disabled className="bg-gray-50 dark:bg-gray-900/50" data-id="element-4997" />
              
                  {canEditProfile && <div className="pt-7" data-id="element-4998">
                      <Button onClick={handleSaveProfile} className="w-full" data-id="element-4999">
                        Guardar Cambios
                      </Button>
                    </div>}
                </div>
              </div>
            </div>
          </div>
        </div>
  }, {
    id: 'preferences',
    label: 'Preferencias',
    icon: SettingsIcon,
    permissionKey: 'settings_preferences' as const,
    content: <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-5000">
          <div className="space-y-6" data-id="element-5001">
            {!canEditPreferences && <ReadOnlyBanner data-id="element-5002" />}
            <div data-id="element-5003">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6" data-id="element-5004">
                Preferencias del Sistema
              </h3>
              <div className="space-y-6" data-id="element-5005">
                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50" data-id="element-5006">
                  <div data-id="element-5007">
                    <p className="font-semibold text-gray-900 dark:text-white" data-id="element-5008">
                      Modo Oscuro
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-5009">
                      Activar tema oscuro para reducir la fatiga visual
                    </p>
                  </div>
                  <label className={`relative inline-flex items-center ${canEditPreferences ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`} data-id="element-5010">
                
                    <input type="checkbox" checked={preferences.darkMode} onChange={e => handleToggleDarkMode(e.target.checked)} className="sr-only peer" disabled={!canEditPreferences} data-id="element-5011" />
                
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 dark:peer-focus:ring-teal-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[#1BACA6]" data-id="element-5012"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50" data-id="element-5013">
                  <div data-id="element-5014">
                    <p className="font-semibold text-gray-900 dark:text-white" data-id="element-5015">
                      Notificaciones de Escritorio
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-5016">
                      Recibir alertas emergentes en el navegador
                    </p>
                  </div>
                  <label className={`relative inline-flex items-center ${canEditPreferences ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`} data-id="element-5017">
                
                    <input type="checkbox" checked={preferences.desktopNotifications} onChange={e => handleToggleDesktopNotifications(e.target.checked)} className="sr-only peer" disabled={!canEditPreferences} data-id="element-5018" />
                
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 dark:peer-focus:ring-teal-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[#1BACA6]" data-id="element-5019"></div>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </Card>
  }, {
    id: 'notifications',
    label: 'Notificaciones',
    icon: BellIcon,
    permissionKey: 'settings_notifications' as const,
    content: <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-5020">
          <div className="space-y-6" data-id="element-5021">
            {!canEditNotifications && <ReadOnlyBanner data-id="element-5022" />}
            <div data-id="element-5023">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6" data-id="element-5024">
                Configuración de Notificaciones
              </h3>
              <div className="space-y-4" data-id="element-5025">
                {[{
              key: 'newAppointments',
              label: 'Nuevas Citas',
              desc: 'Notificar cuando se crea una nueva cita'
            }, {
              key: 'cancelledAppointments',
              label: 'Citas Canceladas',
              desc: 'Notificar cuando se cancela una cita'
            }, {
              key: 'newPatients',
              label: 'Nuevos Pacientes',
              desc: 'Notificar cuando se registra un nuevo paciente'
            }].map(item => <div key={item.key} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50" data-id="element-5026">
              
                    <div data-id="element-5027">
                      <p className="font-semibold text-gray-900 dark:text-white" data-id="element-5028">
                        {item.label}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1" data-id="element-5029">
                        {item.desc}
                      </p>
                    </div>
                    <label className={`relative inline-flex items-center ${canEditNotifications ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`} data-id="element-5030">
                
                      <input type="checkbox" checked={notifications[item.key as keyof NotificationSettings]} onChange={e => canEditNotifications && saveNotifications({
                  ...notifications,
                  [item.key]: e.target.checked
                })} className="sr-only peer" disabled={!canEditNotifications} data-id="element-5031" />
                
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-teal-300 dark:peer-focus:ring-teal-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-[#1BACA6]" data-id="element-5032"></div>
                    </label>
                  </div>)}
              </div>
            </div>
          </div>
        </Card>
  }, {
    id: 'security',
    label: 'Seguridad',
    icon: ShieldIcon,
    permissionKey: 'settings_security' as const,
    content: <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-5033">
          <div className="space-y-6" data-id="element-5034">
            {!canEditSecurity && <ReadOnlyBanner data-id="element-5035" />}
            <div data-id="element-5036">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-6" data-id="element-5037">
                Seguridad de la Cuenta
              </h3>

              {passwordSaved && <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl text-green-600 dark:text-green-400 text-sm flex items-center gap-2" data-id="element-5038">
                  <ShieldCheckIcon className="w-5 h-5" data-id="element-5039" />
                  <span className="font-medium" data-id="element-5040">
                    Contraseña actualizada correctamente
                  </span>
                </div>}

              {passwordError && <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl text-red-600 dark:text-red-400 text-sm flex items-center gap-2" data-id="element-5041">
                  <ShieldIcon className="w-5 h-5" data-id="element-5042" />
                  <span className="font-medium" data-id="element-5043">{passwordError}</span>
                </div>}

              {canEditSecurity ? <div className="space-y-5 max-w-lg" data-id="element-5044">
                  <Input label="Contraseña Actual" type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="••••••••" data-id="element-5045" />
            
                  <Input label="Nueva Contraseña" type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="••••••••" data-id="element-5046" />
            
                  <Input label="Confirmar Nueva Contraseña" type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="••••••••" data-id="element-5047" />
            
                  <div className="pt-2" data-id="element-5048">
                    <Button onClick={handleChangePassword} isLoading={isChangingPassword} data-id="element-5049">
                
                      Cambiar Contraseña
                    </Button>
                  </div>
                </div> : <div className="p-6 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50 text-center" data-id="element-5050">
                  <ShieldIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" data-id="element-5051" />
                  <p className="text-gray-500 dark:text-gray-400" data-id="element-5052">
                    No tienes permisos para cambiar la contraseña. Contacta a un
                    administrador.
                  </p>
                </div>}
            </div>
          </div>
        </Card>
  }, {
    id: 'email',
    label: 'Correos',
    icon: MailIcon,
    permissionKey: 'settings_email' as const,
    content: <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-5053">
          <EmailNotificationsConfig isReadOnly={!canEditEmail} data-id="element-5054" />
        </div>
  }, {
    id: 'verification',
    label: 'Verificación',
    icon: ShieldCheckIcon,
    permissionKey: 'settings_verification' as const,
    content: <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-5055">
          <VerificationCodesConfig isReadOnly={!canEditVerification} data-id="element-5056" />
        </div>
  }, {
    id: 'users',
    label: 'Usuarios',
    icon: UsersIcon,
    permissionKey: 'settings_users' as const,
    content: <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-5057">
          <UserManagement data-id="element-5058" />
        </div>
  }, {
    id: 'roles-permissions',
    label: 'Roles y Permisos',
    icon: KeyIcon,
    permissionKey: 'settings_roles_permissions' as const,
    content: <div className="space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-5059">
          <Roles data-id="element-5060" />
          <div className="border-t border-gray-200 dark:border-gray-700 pt-8" data-id="element-5061">
            <Permissions data-id="element-5062" />
          </div>
        </div>
  }, {
    id: 'backups',
    label: 'Backups',
    icon: DatabaseIcon,
    permissionKey: 'settings_backups' as const,
    content: <div className="animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-5063">
          <BackupConfig data-id="element-5064" />
        </div>
  }];

  // Filter tabs based on user permissions
  const tabs = allTabs.filter(tab => hasPermission(userRole, tab.permissionKey, 'read'));
  // Set default tab if current is invalid
  useEffect(() => {
    if (tabs.length > 0 && !tabs.find(t => t.id === activeTabId)) {
      setActiveTabId(tabs[0].id);
    }
  }, [tabs, activeTabId]);
  const activeTabContent = tabs.find(t => t.id === activeTabId)?.content;
  return <div className="flex flex-col gap-6 min-h-0" data-id="element-5065">
      {/* Header Banner */}
      <div className="relative overflow-hidden rounded-2xl shadow-xl" style={{
      background: 'linear-gradient(135deg, #0f766e, #0d9488, #0891b2)'
    }} text-white data-id="element-5066">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-[0.06] pointer-events-none" style={{
        backgroundImage: 'repeating-linear-gradient(0deg,transparent,transparent 19px,rgba(255,255,255,1) 19px,rgba(255,255,255,1) 20px),repeating-linear-gradient(90deg,transparent,transparent 19px,rgba(255,255,255,1) 19px,rgba(255,255,255,1) 20px)'
      }} data-id="element-5067" />
        {/* Glow */}
        <div className="absolute -top-8 -right-8 w-48 h-48 rounded-full blur-2xl opacity-20 pointer-events-none bg-white" data-id="element-5068" />
        {/* Left accent strip */}
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/30" data-id="element-5069" />
        <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full opacity-20 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, transparent 65%)'
      }} data-id="element-5070" />
        <div className="absolute -bottom-16 -left-8 w-64 h-64 rounded-full opacity-15 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(6,182,212,0.8) 0%, transparent 60%)'
      }} data-id="element-5071" />
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/25" data-id="element-5072" />
        <div className="relative z-10 p-6 pl-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4 w-full" data-id="element-5073">
          <div data-id="element-5074">
            <div className="flex items-center gap-3 mb-2" data-id="element-5075">
              <div className="p-2.5 rounded-xl bg-white/15 border border-white/20 backdrop-blur-sm shadow-inner" data-id="element-5076">
                <SettingsIcon className="w-6 h-6 text-white" data-id="element-5077" />
              </div>
              <h1 className="text-3xl font-bold tracking-tight text-white" data-id="element-5078">
                Configuración
              </h1>
            </div>
            <p className="max-w-xl text-sm font-medium text-white/80" data-id="element-5079">
              Administra tu cuenta, preferencias y seguridad del sistema
            </p>
          </div>
        </div>
      </div>

      {tabs.length > 0 ? <div className="flex flex-col md:flex-row gap-4 md:gap-8" data-id="element-5080">
          {/* Sidebar Navigation */}
          <nav className="w-full lg:w-72 shrink-0 flex md:flex-col gap-2 overflow-x-auto md:overflow-visible pb-2 md:pb-0 px-1" data-id="element-5081">
            {tabs.map(tab => {
          const Icon = tab.icon;
          const isActive = activeTabId === tab.id;
          return <button key={tab.id} onClick={() => setActiveTabId(tab.id)} className={`
                    flex items-center gap-3 px-4 py-3.5 rounded-xl text-sm font-medium transition-all duration-200
                    whitespace-nowrap md:whitespace-normal
                    ${isActive ? 'bg-white dark:bg-gray-800 text-[#1BACA6] shadow-md border border-gray-100 dark:border-gray-700 translate-x-1' : 'text-gray-600 dark:text-gray-400 hover:bg-white/50 dark:hover:bg-gray-800/50 hover:text-gray-900 dark:hover:text-gray-200'}
                  `} data-id="element-5082">
                
                  <Icon className={`w-5 h-5 ${isActive ? 'text-[#1BACA6]' : 'text-gray-400 dark:text-gray-500'}`} data-id="element-5083" />
                
                  <span className="flex-1 text-left" data-id="element-5084">{tab.label}</span>
                  {isActive && <ChevronRightIcon className="w-4 h-4 opacity-50 hidden md:block" data-id="element-5085" />}
                </button>;
        })}
          </nav>

          {/* Content Area */}
          <div className="flex-1 overflow-y-auto pr-2 pb-10 custom-scrollbar" data-id="element-5086">
            {activeTabContent}
          </div>
        </div> : <Card data-id="element-5087">
          <div className="py-16 text-center text-gray-500 dark:text-gray-400" data-id="element-5088">
            <SettingsIcon className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" data-id="element-5089" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2" data-id="element-5090">
              Acceso Restringido
            </h3>
            <p data-id="element-5091">No tienes permisos para ver ninguna sección de configuración.</p>
          </div>
        </Card>}
    </div>;
}